import React, { FC, useCallback, useEffect } from "react";

import { defineMessages, useIntl } from "react-intl";

import { CountryRegion } from "@mapmycustomers/shared/enum";
import User from "@mapmycustomers/shared/types/User";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import useStateWithDebouncedListener from "@mapmycustomers/shared/util/hook/useStateWithDebouncedListener";
import { SearchField } from "@mapmycustomers/ui";

import RoundButton from "@app/component/RoundButton";
import UserFilter from "@app/component/UserFilter";

import styles from "./Filters.module.scss";
import RegionFilter from "./RegionFilter";

const messages = defineMessages({
  creator: {
    id: "map.layer.boundaries.territories.filterModal.filters.creator.title",
    defaultMessage: "Creator{hasValue, select, true {: {label}} other {}}",
    description: "Title of the Creator filter button on the Filter Territories modal",
  },
  quickFilterPlaceholder: {
    id: "map.layer.boundaries.territories.filterModal.filters.quick",
    defaultMessage: "Search territories",
    description: "Quick filter placeholder on the Filter Territories modal",
  },
  selected: {
    id: "map.layer.boundaries.territories.filterModal.filters.selected.title",
    defaultMessage: "Selected",
    description: "Title of the Selected filter button on the Filter Territories modal",
  },
  sharedWith: {
    id: "map.layer.boundaries.territories.filterModal.filters.sharedWith.title",
    defaultMessage: "Shared with",
    description: "Title of the Shared with filter button on the Filter Territories modal",
  },
});

export interface FiltersProps {
  creatorIds: User["id"][];
  onCreatorIdsChange: (creatorIds: User["id"][]) => void;
  onQuickFilterChange: (value: string) => void;
  onRegionChange: (value?: CountryRegion) => void;
  onSelectedOnlyChange: (selectedOnly: boolean) => void;
  onSharedWithChange: (userIds: User["id"][]) => void;
  quickFilter: string;
  region?: CountryRegion;
  selectedOnly: boolean;
  sharedWith: User["id"][];
}

const Filters: FC<FiltersProps> = ({
  creatorIds,
  onCreatorIdsChange,
  onQuickFilterChange,
  onRegionChange,
  onSelectedOnlyChange,
  onSharedWithChange,
  quickFilter,
  region,
  selectedOnly,
  sharedWith,
}) => {
  const intl = useIntl();

  const [innerValue, setInnerValue, resetInnerValue] = useStateWithDebouncedListener(
    [onQuickFilterChange, 100],
    ""
  );
  useEffect(() => {
    resetInnerValue(quickFilter);
  }, [quickFilter, resetInnerValue]);

  const handleEnterPress = useDynamicCallback(() => {
    onQuickFilterChange(innerValue);
  });

  const handleSelectedClick = useCallback(() => {
    onSelectedOnlyChange(!selectedOnly);
  }, [selectedOnly, onSelectedOnlyChange]);

  return (
    <div className={styles.container}>
      <SearchField
        className={styles.quickSearch}
        onChange={setInnerValue}
        onPressEnter={handleEnterPress}
        placeholder={intl.formatMessage(messages.quickFilterPlaceholder)}
        value={innerValue}
      />
      <div className={styles.filters}>
        <UserFilter onChange={onCreatorIdsChange} title={messages.creator} value={creatorIds} />
        <RegionFilter onChange={onRegionChange} value={region} />
        <UserFilter onChange={onSharedWithChange} title={messages.sharedWith} value={sharedWith} />
        <RoundButton active={selectedOnly} onClick={handleSelectedClick}>
          {intl.formatMessage(messages.selected)}
        </RoundButton>
      </div>
    </div>
  );
};

export default Filters;
